<template>
  <div class="card mt-3">
    <div class="card-body">
      <div class="d-flex flex-wrap column-gap-3 row-gap-1 small mb-2">
        <div v-for="l in LAYERS" :key="l.key" class="form-check form-check-inline m-0">
          <input :id="`map-layer-${l.key}`" v-model="visible[l.key]" class="form-check-input" type="checkbox" />
          <label class="form-check-label" :for="`map-layer-${l.key}`">
            <span class="swatch" :class="l.shape" :style="{ backgroundColor: l.color }"></span>{{ l.text }}
          </label>
        </div>
        <span v-if="day && !day.trackers?.length" class="text-muted">Ni vozila za GPS sled.</span>
      </div>
      <GoogleMap
        ref="mapEl"
        mapId="mapEl"
        :api-key="$window.GOOGLE_API_KEY"
        style="width: 100%; height: 60vh; min-height: 400px"
        :center="CENTER"
        :zoom="8"
      >
        <template v-if="visible.track">
          <Polyline
            v-for="(path, idx) in tracks"
            :key="'track-' + idx"
            :options="{ path, strokeColor: COLORS.track, strokeOpacity: 0.8, strokeWeight: 3 }"
          />
        </template>
        <AdvancedMarker v-for="m in visibleMarkers" :key="m.key" :options="m.options" />
      </GoogleMap>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue"
import dayjs from "dayjs"
import { GoogleMap, AdvancedMarker, Polyline } from "vue3-google-map"
import { useAuth } from "../composition-api/useAuth"
import { listVehicleTrackAsync } from "../api"
import { createMarkerElement } from "scripts/utils/mapMarkers"

defineOptions({ name: "AbsenceDayMap" })

const props = defineProps({
  day: { type: Object, default: null }, // AbsenceDay response; null while loading
  date: { type: Object, required: true }, // dayjs
})

const { fetchAuthAsync } = useAuth()

const CENTER = { lat: 46.05, lng: 14.93 }
const COLORS = { track: "#198754", plan: "#6c757d", transports: "#6f42c1" }
const LAYERS = [
  { key: "track", text: "GNSS sled", color: COLORS.track, shape: "line" },
  { key: "plan", text: "Plan", color: COLORS.plan, shape: "circle" },
  { key: "transports", text: "Vpisane poti", color: COLORS.transports, shape: "square" },
]
const visible = reactive({ track: true, plan: true, transports: true })

const mapEl = ref(null)
const tracks = ref([]) // one path per tracker

const time = (value) => (value ? dayjs(value).format("HH:mm") : "")
const point = (lat, lng) => (lat != null && lng != null ? { lat: +lat, lng: +lng } : null)

const markers = computed(() => {
  if (!props.day) return []
  const result = []
  const add = (layer, key, position, color, label, title, shape) => {
    if (position) {
      result.push({
        layer,
        key: `${layer}-${key}`,
        options: { position, title, content: createMarkerElement(color, label, false, shape) },
      })
    }
  }

  props.day.waypoints.forEach((wp, i) =>
    add(
      "plan",
      wp.id,
      point(wp.lat, wp.lng),
      COLORS.plan,
      i + 1,
      `${i + 1}. ${wp.time?.slice(0, 5) ?? ""} ${[wp.serviceOrderNumber, wp.name].filter(Boolean).join(" ")}`,
    ),
  )
  props.day.transports.forEach((t, i) => {
    const what = [t.orderNumber, t.facility, t.relation].filter(Boolean).join(" ")
    const title = `${time(t.startDateTime)} – ${time(t.endDateTime)} ${what}`
    const start = point(t.startLat, t.startLng)
    const end = point(t.endLat, t.endLng)
    add("transports", `${t.id}-start`, start, COLORS.transports, i + 1, `Začetek ${title}`, "square")
    add("transports", `${t.id}-end`, end, COLORS.transports, i + 1, `Konec ${title}`, "square")
  })
  return result
})
const visibleMarkers = computed(() => markers.value.filter((m) => visible[m.layer]))

const loadTracksAsync = async () => {
  tracks.value = []
  const day = props.day
  if (!day?.trackers?.length) return
  const date = props.date.format("YYYY-MM-DD")
  const results = await Promise.all(day.trackers.map((tracker) => listVehicleTrackAsync(fetchAuthAsync, tracker, date)))
  if (day !== props.day) return // another day was opened meanwhile
  tracks.value = results.filter((path) => path.length > 1)
}

// zoom to everything shown for the day
const fitBounds = () => {
  const map = mapEl.value?.map
  const points = [...tracks.value.flat(), ...markers.value.map((m) => m.options.position)]
  if (!map || !mapEl.value.ready || !points.length) return
  map.fitBounds(
    {
      north: Math.max(...points.map((p) => p.lat)),
      south: Math.min(...points.map((p) => p.lat)),
      east: Math.max(...points.map((p) => p.lng)),
      west: Math.min(...points.map((p) => p.lng)),
    },
    40,
  )
}

watch(() => props.day, loadTracksAsync, { immediate: true })
watch([() => mapEl.value?.ready, markers, tracks], fitBounds)
</script>

<style scoped>
.swatch {
  display: inline-block;
  width: 0.9em;
  height: 0.9em;
  margin-right: 0.3em;
  vertical-align: -0.1em;
}
.swatch.circle {
  border-radius: 50%;
}
.swatch.square {
  border-radius: 0.15em;
}
.swatch.line {
  height: 0.25em;
  vertical-align: 0.2em;
}
</style>
